import React, { useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useInjection } from 'services/ioc/ioc.react';
import { LineupBatter } from 'pages/Tagging/components/Lineup/LineupBatter/LineupBatter';
import { BpTaggingStore } from 'stores/Tagging/BpTaggingStore';
import { ServiceTypes } from 'constants/ServiceTypes';
import { SessionStore } from 'stores/SessionStore';
import { SectionHeadline } from 'pages/Tagging/components/SectionHeadline/SectionHeadline';
import { SectionHeader } from 'pages/Tagging/components/SectionHeader/SectionHeader';
import { SearchBar } from 'components/ui/SearchBar/SearchBar';
import { apiLineupPlayerToPlayerMap } from 'services/helper-services/mappingHelpers/playerMappings';
import { IPlayer } from 'interfaces/team/IPlayer';
import mappingHelpers from 'services/helper-services/mappingHelpers/mappingHelpers';
import EditLineupModal from 'components/modals/EditLineupModal/EditLineupModal';
import { PlayerSearchService, IApiLineupPlayer } from '@trackman/baseball-shared-components';
import { ApiLineupDraggablePlayer } from 'interfaces/team/ApiLineupDraggablePlayer';
import { Batters, BpLineupListWrapper, StyledEditLineupIcon, TaggingColumn } from './BpLineupListStyles';

interface IProps {
  disabled?: boolean;
}

const BpLineupList: React.FC<IProps> = observer(({ disabled }) => {
  const taggingStore = useInjection<BpTaggingStore>(ServiceTypes.BpTaggingStore);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  if (!taggingStore.isInitialized) {
    return null;
  }
  const ref = useRef<any>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchService, setSearchService] = useState<PlayerSearchService | undefined>(undefined);
  const [searchResults, setSearchResults] = useState<IPlayer[]>([]);
  const { selectedPlay, placeholderPlay } = taggingStore;
  const displayPlay = selectedPlay || placeholderPlay;
  const isReadOnlyMode = !selectedPlay || disabled;
  const batterList = displayPlay.playLineup.players;
  const [currentBatterId, setCurrentBatterId] = useState<string>(batterList[0].id);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);

  useEffect(() => {
    setSearchService(new PlayerSearchService(batterList.map(apiLineupPlayerToPlayerMap)));
  }, [batterList]);

  useEffect(() => {
    setCurrentBatterId(selectedPlay ? selectedPlay.batter.id : batterList[0].id);
  }, [selectedPlay, selectedPlay && selectedPlay.batter]);

  useEffect(() => {
    searchService && setSearchResults(searchService.searchPlayersByName(searchQuery));
  }, [searchQuery, searchService]);

  const changePlayerOnSelectedPlay = (playerId: string, lineup: IApiLineupPlayer[]) => {
    if (isReadOnlyMode) {
      return;
    }
    const player = lineup.find((x) => x.id === playerId);
    if (selectedPlay && player) {
      // TODO: CHECK THIS PLAYER
      // @ts-ignore
      selectedPlay.batter = player;
      selectedPlay.isChanged = true;
    }
  };

  const editLineup = async (newLineup: ApiLineupDraggablePlayer[]) => {
    await taggingStore.updateLineup(displayPlay, newLineup);
    setIsEditModalVisible(false);
  };

  return (
    <BpLineupListWrapper>
      <EditLineupModal
        isVisible={isEditModalVisible}
        onDismiss={() => setIsEditModalVisible(false)}
        changeLineupAsync={editLineup}
        currentLineup={batterList}
        roster={taggingStore.teamRoster}
        team={mappingHelpers.teamWithLineupToTeamMap(sessionStore.currentSessionMetadata.homeTeam)}
        ruleset={sessionStore.currentSessionMetadata.ruleSet}
        unavailablePlayerIds={[]}
      />
      <SectionHeadline>
        Lineup <StyledEditLineupIcon onClick={() => setIsEditModalVisible(true)} />
      </SectionHeadline>
      <TaggingColumn className='tagging-column'>
        <div ref={ref} style={{ height: '100%' }}>
          <SectionHeader title={sessionStore.currentSessionMetadata.homeTeam?.name ?? ''} />
          <SearchBar
            searchQuery={searchQuery}
            setSearchQueryCallback={(value: string) => setSearchQuery(value)}
            placeholder='Search for player'
          />
          <Batters>
            {batterList
              .filter((p) => !searchQuery || searchResults.some((r) => r.id === p.id))
              .map((item, index) => (
                <LineupBatter
                  key={index}
                  player={item}
                  isCurrent={item.id === currentBatterId}
                  index={(index + 1).toString()}
                  onClickPlayerCallback={(playerId) => changePlayerOnSelectedPlay(playerId, batterList)}
                  disabled={isReadOnlyMode}
                  isBattingPractice
                />
              ))}
          </Batters>
        </div>
      </TaggingColumn>
    </BpLineupListWrapper>
  );
});

export default BpLineupList;
